import React, { ReactNode, useMemo } from "react";
import { Col, DatePicker, Divider, Form, Input, Row, Select, Spin, Tooltip } from "antd";
import moment from "moment";
import { FormInstance } from "antd/es/form/Form";
import { useAppSelector } from "../../../..";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  getSelectOptions,
  toDisplayFormat,
} from "../../../../lib/utils";
import { IChangingMode } from "../../../../lib/types";
import { IChangingRecordKeyNumber } from "../../../../lib/types/IViewMode";
import { ReactComponent as EditIcon } from "../../../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as CancelButtonIcon } from "../../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import {
  MENU_ADMIN_OFFERING_GROUP_WRITE,
  POLICY_EXTENDED_EDIT,
} from "../../../../lib/const/permissions";
import { IOfferingGroup, IOfferingGroupForm } from "../../types/IOfferingGroup";
import "./OfferingGroupParams.less";

interface ComponentProps {
  mode: "create" | "view" | "edit_close_date" | "edit";
  childChangingMode?: IChangingRecordKeyNumber;
  offeringGroup?: IOfferingGroup;
  offeringGroupParamsForm?: FormInstance<IOfferingGroupForm>;
  isLoading?: boolean;
  isChild?: boolean;
  idx?: number;
  isLatestVersion?: boolean;
  isEditingDisabled?: boolean;
  onChangePolicyType?: (id: number) => void;
  changeViewMode?: (params?: IChangingRecordKeyNumber) => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  setActiveOfferingGroupVersion?: (value: React.SetStateAction<IOfferingGroup | undefined>) => void;
}

export const OfferingGroupParams = ({
  mode,
  childChangingMode,
  offeringGroup,
  offeringGroupParamsForm,
  isLoading = false,
  isChild = false,
  idx,
  isLatestVersion,
  isEditingDisabled,
  onChangePolicyType,
  changeViewMode,
  onCancel,
  onSubmit,
  setActiveOfferingGroupVersion,
}: ComponentProps) => {
  const { offeringGroupsTypesDic, offeringGroupsTypesDicLoading } = useAppSelector(
    (state) => state.offeringGroups
  );

  const isCurrentVersion = useMemo(() => {
    const fromDate = offeringGroup?.fromDate && moment(offeringGroup.fromDate);
    const toDate = offeringGroup?.toDate && moment(offeringGroup.toDate);
    if (fromDate && toDate) {
      return moment().isBetween(fromDate, toDate);
    }
    return false;
  }, [offeringGroup]);

  const isPastVersion = useMemo(() => {
    const fromDate = offeringGroup?.fromDate && moment(offeringGroup.fromDate);
    const toDate = offeringGroup?.toDate && moment(offeringGroup.toDate);
    if (fromDate && toDate) {
      return toDate.isBefore(moment());
    }
    return false;
  }, [offeringGroup]);

  const renderDataItem = (props: {
    label: string;
    data?: ReactNode;
    offsetCol?: number;
    spanCol?: number;
    labelCol?: number;
    wrapperOffset?: number;
    wrapperCol?: number;
  }): ReactNode => {
    const {
      label,
      data,
      offsetCol = 0,
      spanCol = 6,
      labelCol,
      wrapperOffset,
      wrapperCol = 16,
    } = props;
    return (
      <Col offset={offsetCol} span={spanCol}>
        <Row className="offering-group-params__dataRow">
          <Col span={labelCol}>
            <label>{label}</label>
          </Col>
          <Col offset={wrapperOffset} span={wrapperCol}>
            <span>{data}</span>
          </Col>
        </Row>
      </Col>
    );
  };

  const handleSetViewMode = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    mode: IChangingMode
  ) => {
    e.stopPropagation();
    if (mode === "edit") {
      if (offeringGroupParamsForm) {
        offeringGroupParamsForm.setFieldsValue({
          name: offeringGroup?.name,
          groupingTypeId: offeringGroup?.groupingTypeId,
          fromDate: moment(offeringGroup?.fromDate),
          toDate: moment(offeringGroup?.toDate),
        });
      }
    }
    if (mode === "add") {
      if (offeringGroupParamsForm) {
        offeringGroupParamsForm.setFieldsValue({
          name: offeringGroup?.name,
          groupingTypeId: offeringGroup?.groupingTypeId,
          fromDate: moment(offeringGroup?.fromDate).add(1, "d"),
          toDate: moment(offeringGroup?.toDate),
        });
      }
    }
    if (mode === "close") {
      if (offeringGroupParamsForm) {
        offeringGroupParamsForm.setFieldsValue({
          toDate: moment(offeringGroup?.toDate),
          fromDate: moment(offeringGroup?.fromDate),
        });
      }
    }
    setActiveOfferingGroupVersion && setActiveOfferingGroupVersion(offeringGroup);
    changeViewMode && idx !== undefined && changeViewMode([idx, mode]);
  };

  const handleOnCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();

    if (offeringGroupParamsForm) {
      offeringGroupParamsForm.setFieldsValue({
        name: offeringGroup?.name,
        groupingTypeId: offeringGroup?.groupingTypeId,
        fromDate: moment(offeringGroup?.fromDate),
        toDate: moment(offeringGroup?.toDate),
      });
    }

    onCancel && onCancel();
  };

  const handleOnSubmit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onSubmit && onSubmit();
  };

  const renderButtons = () => {
    if (mode === "edit" || mode === "edit_close_date") {
      return (
        <Row gutter={7} justify="end">
          <Col>
            <Tooltip title="Подтвердить">
              <SubmitButtonIcon
                onClick={handleOnSubmit}
                className="offering-group-params__iconBtn"
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title="Отменить">
              <CancelButtonIcon
                onClick={handleOnCancel}
                className="offering-group-params__iconBtn"
              />
            </Tooltip>
          </Col>
        </Row>
      );
    }

    return isChild && !isEditingDisabled && hasAccess(MENU_ADMIN_OFFERING_GROUP_WRITE) ? (
      <Row gutter={7} justify="end">
        {isLatestVersion && (
          <Col>
            <CloseVersionIcon
              onClick={(e) => handleSetViewMode(e, "close")}
              className="offering-group-params__iconBtn"
            />
          </Col>
        )}
        {!isPastVersion && (
          <>
            <Col>
              {isLatestVersion && (
                <Tooltip title="Добавить версию">
                  <AddVersionIcon
                    onClick={(e) => handleSetViewMode(e, "add")}
                    className="offering-group-params__iconBtn"
                  />
                </Tooltip>
              )}
            </Col>
            <Col>
              <Tooltip title="Редактировать">
                <EditIcon
                  onClick={(e) => handleSetViewMode(e, "edit")}
                  className="offering-group-params__iconBtn"
                />
              </Tooltip>
            </Col>
          </>
        )}
      </Row>
    ) : null;
  };

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    if (isCurrentVersion) return `row_child row_current-version`;
    if (isPastVersion) return "row_child row_past-version";
    return "row_child";
  }, [offeringGroup, isChild]);

  const colSpans = {
    name: 7,
    groupingTypeId: 4,
    fromDate: 5,
    toDate: 5,
    btns: 3,
  };

  return (
    <Row className={`offering-group-params ${childClassName}`} style={{ width: "100%" }}>
      {mode !== "create" && <Divider style={{ margin: "0 0 9px" }} />}
      <Spin spinning={isLoading}>
        <Row gutter={50} style={{ width: "100%" }}>
          {mode === "create" || mode === "edit" ? (
            <Col span={colSpans.name}>
              <Form.Item
                label="Наименование"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
                className={mode === "edit" ? "not-bottom" : ""}
                labelCol={{ offset: mode === "create" ? 1 : 0 }}
                wrapperCol={{ offset: 1, span: 16 }}
              >
                <Input maxLength={255} placeholder="Введите текст..." allowClear />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Наименование",
                data: offeringGroup?.name,
                spanCol: colSpans.name,
                wrapperOffset: 1,
              })}
            </>
          )}

          {mode === "create" || mode === "edit" ? (
            <Col span={colSpans.groupingTypeId}>
              <Form.Item
                label="Тип"
                name="groupingTypeId"
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
                className={mode === "edit" ? "not-bottom" : ""}
                wrapperCol={{ offset: 1, span: 18 }}
              >
                <Select
                  options={getSelectOptions({
                    list: offeringGroupsTypesDic,
                  })}
                  loading={checkIsLoading(offeringGroupsTypesDicLoading)}
                  style={{ width: "110%" }}
                  placeholder="Выбрать"
                  allowClear
                  onChange={(e) => {
                    onChangePolicyType && onChangePolicyType(e as number);
                    offeringGroupParamsForm &&
                      offeringGroupParamsForm.resetFields(["actionId", "newOfferingName"]);
                  }}
                  disabled={mode === "edit" && !hasAccess(POLICY_EXTENDED_EDIT)}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Тип",
                data: getDictionaryItemById(offeringGroupsTypesDic, offeringGroup?.groupingTypeId)
                  ?.name,
                spanCol: colSpans.groupingTypeId,
                wrapperOffset: 1,
              })}
            </>
          )}

          {mode === "create" || (mode === "edit" && childChangingMode?.[1] === "add") ? (
            <Col span={colSpans.fromDate}>
              <Form.Item dependencies={["toDate"]} noStyle>
                {({ getFieldValue }) => {
                  const toDate = getFieldValue("toDate");
                  return (
                    <Form.Item
                      label="Период с"
                      name="fromDate"
                      rules={[
                        {
                          required: true,
                          message: "",
                        },
                      ]}
                      className={mode === "edit" ? "not-bottom" : ""}
                      wrapperCol={{ offset: 1, span: 14 }}
                    >
                      <DatePicker
                        allowClear={false}
                        disabledDate={(current) => {
                          if (mode === "edit") {
                            return (
                              current.isAfter(moment(toDate), "day") ||
                              current.isBefore(moment(offeringGroup?.fromDate).add(1, "d"), "day")
                            );
                          }
                          return toDate ? current.isAfter(toDate, "day") : false;
                        }}
                        format={displayFormat}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Период с",
                data: offeringGroup?.fromDate && toDisplayFormat(offeringGroup?.fromDate),
                spanCol: colSpans.fromDate,
                wrapperOffset: 1,
              })}
            </>
          )}

          {mode === "create" || mode === "edit_close_date" ? (
            <Col span={colSpans.toDate}>
              <Form.Item dependencies={["fromDate"]} noStyle>
                {({ getFieldValue }) => {
                  const fromDate = getFieldValue("fromDate");
                  const toDate = getFieldValue("toDate");
                  return (
                    <Form.Item
                      label="Период по"
                      name="toDate"
                      rules={[
                        {
                          required: mode === "edit_close_date",
                          message: "",
                        },
                      ]}
                      className={mode === "edit_close_date" ? "not-bottom" : ""}
                      wrapperCol={{ offset: 1, span: 14 }}
                    >
                      <DatePicker
                        allowClear={false}
                        disabledDate={(current) => {
                          if (mode === "edit_close_date") {
                            return fromDate
                              ? current.isBefore(fromDate, "day") || current.isAfter(toDate, "day")
                              : false;
                          }
                          return fromDate ? current.isBefore(fromDate, "day") : false;
                        }}
                        format={displayFormat}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Период по",
                data: offeringGroup?.toDate && toDisplayFormat(offeringGroup?.toDate),
                spanCol: colSpans.toDate,
                wrapperOffset: 1,
              })}
            </>
          )}

          <Col span={colSpans.btns}>{renderButtons()}</Col>
        </Row>
      </Spin>
    </Row>
  );
};

export default OfferingGroupParams;
